import ErrorComponent from 'Components/ErrorComponent';
import ErrorTooltip from 'Components/ErrorTooltip';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import env from 'environment_system/env_system';
import { FormikProps, useFormik } from 'formik';
import { checkEmptyStringValue, handleServerResponse, isConfigurationButtonDisabled } from 'GenericForms/Helper';
import apiGlobal from 'global/api.global';
import { RootState } from 'index';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { queryClient } from 'react-query/queryClient';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { Button, CardBody, Col, Form, Input, Label, Row } from "reactstrap";
import { customStyle } from 'shared/CommonCSS';
import { OnboardingStatus, Roles, VesselConfigrationTabs, VesselTypeConstant } from 'shared/constants';
import { queryKeyes } from 'shared/queryKeys';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import * as Yup from 'yup';
import isEqual from "fast-deep-equal";
import { successToast } from 'Components/Toasts';
import { hasRole } from 'utils/auth/authUtils';
import PopOver from 'Components/PopOver';
import FormLoading from 'Components/FormLoading';

interface OtherOtherSettingFormik {
    setCheckValuesBeforeSwitch: (value: boolean) => void;
    setTabName: (value: string) => void;
    VesselConfActiveTab: any;
    setFormik?: (value: FormikProps<any>) => void;
    formik?: FormikProps<any>;
    refreshForm?: boolean;
    setDisableYesButton?: (value: boolean) => void;
    saveBeforeChangePopUp?: boolean;
}

const Other = ({ setCheckValuesBeforeSwitch, setTabName, VesselConfActiveTab, setFormik, refreshForm, setDisableYesButton, saveBeforeChangePopUp }: OtherOtherSettingFormik) => {
    /** State variables */
    const { VesselID, Vessels } = useSelector((state: RootState) => state.Reporting);
    const [count, setCount] = useState(0);
    const [windRefreshkey, setWindRefreshKey] = useState(0);
    const [EEDIRefreshkey, setEEDIRefreshKey] = useState(0);
    const [popoverBol, setPopoverBol] = useState(false);
    const fwindOptions = [
        { value: 0.95, label: 0.95 },
        { value: 0.97, label: 0.97 },
        { value: 0.99, label: 0.99 },
    ];
    let vessel = Vessels.find((rec: any) => rec.id === VesselID) ?? null;
    const [reeferRefreshKey, setReeferRefreshKey] = useState(0);
    /** State variables end */

    /** Queries */
    /** Vessel's other setting;s object used for edit */
    const { data: OtherSettings, isLoading: OtherSettingsLoading, isError: OtherSettingsError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.VesselOtherSettingsObject.key, VesselID],
            async () => { return await Get(queryKeyes.vessel.VesselOtherSettingsObject.url(VesselID), VesselID) },
            {
                enabled: true,
                staleTime: Infinity,
            }
        );
    /** End Queries */

    /** Assign initial values to formik object */
    const getInitialValues = () => {
        if (OtherSettings && OtherSettings[0]?.id > 0) {
            return OtherSettings[0];
        } else {
            return ({
                is_vse_mention: false,
                vse_value: null,
                reefer_container_name: "",
                is_wind_assistance_system_available: false,
                pwind: null,
                fwind: null,
                pprop: null,
                power_rating: null,
                is_steam_dump_valve_in_use: false,
                reefer_container_available: false,
                is_container_available: vessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER ? true : false,
                max_teu: null,
                vessel: VesselID
            })
        }
    }

    /** Other Setting's formik object */
    const OtherSettingFormik: any = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema: Yup.object({
            vse_value: Yup.number().nullable().when('is_vse_mention', {
                is: true,
                then: (schema: any) => schema.required(commonValidationMessages.required),
                otherwise: (schema: any) => schema.nullable(),
            }),
            pwind: Yup.number().nullable(),
            pprop: Yup.number().nullable(),
            fwind: Yup.number().when('is_wind_assistance_system_available', {
                is: true,
                then: (schema: any) => schema.required(commonValidationMessages.required),
                otherwise: (schema: any) => schema.nullable(),
            }).max(1, commonValidationMessages.max1).min(0, commonValidationMessages.min0),
            max_teu: Yup.number().when('is_container_available', {
                is: true,
                then: (schema) =>
                    schema
                        .typeError(commonValidationMessages.validNumber)
                        .max(999999, commonValidationMessages.max6)
                        .min(0, commonValidationMessages.min0)
                        .required(commonValidationMessages.required),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),
        }),
        onSubmit: async () => {
            OtherSettingFormik.setSubmitting(true);
            let responseArray: any[] = [];
            if (!OtherSettingFormik?.values?.is_vse_mention) {
                OtherSettingFormik.values.vse_value = null; // Reset to empty string if unchecked
            }
            if (!OtherSettingFormik?.values?.is_wind_assistance_system_available) {
                OtherSettingFormik.values.fwind = null; // Reset to empty string if unchecked
                OtherSettingFormik.values.pwind = null; // Reset to empty string if unchecked
                OtherSettingFormik.values.pprop = null;
            }
            if (OtherSettings && OtherSettings[0]?.id > 0) {
                responseArray.push(apiGlobal.put(`/other_setting/${OtherSettingFormik?.values?.id}/`, OtherSettingFormik?.values));
            } else {
                responseArray.push(apiGlobal.post(`/other_setting/`, OtherSettingFormik?.values));
            }
            /** handle server response */
            await handleServerResponse(responseArray).then(async (res) => {
                if (res) {
                    await queryClient.invalidateQueries(queryKeyes.vessel.VesselOtherSettingsObject.key);
                    await queryClient.invalidateQueries(queryKeyes.vessel.ConfigurationList.key);
                    setCount(count + 1);
                }
            });
            OtherSettingFormik.setSubmitting(false);
        }
    })

    if (OtherSettingsError) {
        setCount(count + 1);
    }

    /** Handle formik state changes */
    const handleFormikStateChange = (values: any, initialValues: any) => {
        const areValuesEqual = isEqual(values, initialValues);
        setCheckValuesBeforeSwitch(areValuesEqual);
        setTabName('Other');
        return areValuesEqual;
    };

    // useEffect(() => {
    //     if (VesselConfActiveTab === VesselConfigrationTabs.OTHER && formik !== OtherSettingFormik) {
    //         setFormik(OtherSettingFormik);
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [VesselConfActiveTab, formik, OtherSettingFormik]);
    useEffect(() => {
        if (VesselConfActiveTab === VesselConfigrationTabs.OTHER && OtherSettingFormik.values !== OtherSettingFormik.initialValues) {
            Object.keys(OtherSettingFormik?.errors).length > 0 ? setDisableYesButton(true) : setDisableYesButton(false);
            setFormik(OtherSettingFormik); // Example: Assign formik instance for the "Other" tab
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [VesselConfActiveTab, OtherSettingFormik, OtherSettingFormik?.errors]);

    useEffect(() => {
        if (VesselConfActiveTab !== VesselConfigrationTabs.OTHER) {
            OtherSettingFormik?.setTouched({})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [VesselConfActiveTab]);
    useEffect(() => {
        handleFormikStateChange(OtherSettingFormik.values, OtherSettingFormik.initialValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [OtherSettingFormik.values, OtherSettingFormik.initialValues]);
    useEffect(() => {
        if (vessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER) {
            OtherSettingFormik?.setFieldValue("is_container_available", true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vessel])

    if (OtherSettingsLoading) {
        return <FormLoading />
    } else if (OtherSettingsError) {
        return <ErrorComponent message='Unable to load required data!' />
    } else {
        return (
            <CardBody className='p-0'>
                <div className='ele_row1'>
                    <h4 className="mb-0 navbar_menu">Other Settings</h4>
                    {hasRole(Roles.ES_ADMIN) &&
                        <React.Fragment>
                            <Button
                                type="button"
                                color="primary"
                                id='set_onboarding_status'
                                className='btn bt-primary btn_size_cstm pos-end'
                                onClick={async () => {
                                    setPopoverBol(true);
                                }}
                                disabled={OnboardingStatus.ONBOARDED === vessel?.onboarding_status || OnboardingStatus.CONFIGURED === vessel?.onboarding_status}
                            >
                                Set onboarding status
                            </Button>
                        </React.Fragment>
                    }
                </div>
                {popoverBol &&
                    <PopOver
                        state={popoverBol}
                        setState={setPopoverBol}
                        target="set_onboarding_status"
                        message={`Are you sure you want to set the onboarding status to ${OnboardingStatus.CONFIGURED}?`}
                        handleClick={async () => {
                            apiGlobal.patch(`/vessel_master/${VesselID}/`,
                                {
                                    onboarding_status: OnboardingStatus.CONFIGURED
                                }
                            ).then((res: any) => {
                                if (res.status === 200) {
                                    successToast("Vessel onboarding status updated successfully");
                                }
                            }).catch((err: any) => {
                                console.error(err);
                            })
                            await queryClient.invalidateQueries(queryKeyes.pagination.vesselListPage.key);
                            await queryClient.invalidateQueries(queryKeyes.vessel.Vessels.key);
                            setPopoverBol(false);
                        }}
                    />
                }
                {!OtherSettingsLoading &&
                    <Form autoComplete="off" onSubmit={OtherSettingFormik?.handleSubmit} noValidate key={`${refreshForm}-${count}`}>
                        <Row className='mb-2'>
                            <Col sm={8}>
                                {/* Label & Switch aligned in a row */}
                                <div className="d-flex align-items-center mt-4">
                                    <Label className="me-2 mb-0 width-17" for="is_vse_mention">
                                        Is f<sub>iVSE</sub> mentioned in EEDI / EEXI?
                                    </Label>
                                    <div className="square-switch sqswitch">
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            type="checkbox"
                                            switch="none"
                                            name="is_vse_mention"
                                            id="is_vse_mention"
                                            onChange={(e: any) => {
                                                OtherSettingFormik?.setFieldValue("is_vse_mention", e.target.checked);
                                                if (!e.target.checked) {
                                                    OtherSettingFormik?.setFieldValue("vse_value", null);
                                                    setEEDIRefreshKey(EEDIRefreshkey + 1);
                                                }
                                            }}
                                            checked={OtherSettingFormik?.values?.is_vse_mention}
                                            disabled={isConfigurationButtonDisabled(vessel)}
                                        />
                                        <Label for="is_vse_mention" data-on-label="Yes" data-off-label="No" className="mb-0" />
                                    </div>
                                </div>

                                {/* Input field below */}
                                <div className="w-100 mt-2">
                                    <Label className="mb-0 asteric" for="vse_value">
                                        f<sub>iVSE</sub> value
                                    </Label>
                                    <Input
                                        onFocus={(e) => e.target.select()}
                                        type="text"
                                        name="vse_value"
                                        id="vse_value"
                                        key={EEDIRefreshkey}
                                        value={OtherSettingFormik?.values?.vse_value}
                                        onChange={(e: any) => {
                                            checkEmptyStringValue(e, "vse_value", OtherSettingFormik?.setFieldValue);
                                        }}
                                        onBlur={OtherSettingFormik?.handleBlur}
                                        className="form-control max-width-7 text-right mb-2"
                                        disabled={!OtherSettingFormik?.values?.is_vse_mention}
                                    />
                                    {OtherSettingFormik?.errors &&
                                        env?.form_validation === true &&
                                        OtherSettingFormik?.touched?.vse_value &&
                                        OtherSettingFormik?.values?.is_vse_mention === true && (
                                            <ErrorTooltip
                                                target="vse_value"
                                                message={OtherSettingFormik?.errors?.vse_value}
                                                open={
                                                    !saveBeforeChangePopUp &&
                                                        VesselConfActiveTab === VesselConfigrationTabs.OTHER &&
                                                        OtherSettingFormik?.errors?.vse_value
                                                        ? true
                                                        : false
                                                }
                                            />
                                        )}
                                </div>
                            </Col>
                        </Row>
                        <Row className='mb-2'>
                            <Col sm={10}>
                                <div className="d-flex align-items-center ">
                                    <Label className='width-17 mb-0' for='is_wind_assistance_system_available'>
                                        Is wind assistance system available?
                                    </Label>
                                    <div className="square-switch sqswitch ms-2">
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            type="checkbox"
                                            switch="none"
                                            name="is_wind_assistance_system_available"
                                            id="is_wind_assistance_system_available"
                                            onChange={(e: any) => {
                                                OtherSettingFormik?.setFieldValue("is_wind_assistance_system_available", e.target.checked)
                                                if (!e.target.checked) {
                                                    OtherSettingFormik?.setFieldValue('fwind', null);
                                                    OtherSettingFormik?.setFieldValue('pwind', null);
                                                    OtherSettingFormik?.setFieldValue('pprop', null);
                                                    setWindRefreshKey(windRefreshkey + 1);
                                                }
                                            }}
                                            checked={OtherSettingFormik?.values?.is_wind_assistance_system_available}
                                            disabled={isConfigurationButtonDisabled(vessel)}
                                        />
                                        <Label
                                            for="is_wind_assistance_system_available"
                                            data-on-label='Yes'
                                            data-off-label='No'
                                            className='mb-0'
                                        />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <Row>
                                        <Col sm={3}>
                                            <Label className='mb-0 asteric' htmlFor='fwind'>f<sub>wind</sub></Label>
                                            <Select
                                                key={windRefreshkey}
                                                inputId='fwind'
                                                id='fwind'
                                                name={'fwind'}
                                                options={fwindOptions as any[]}
                                                getOptionLabel={(option: any) => option.label}
                                                getOptionValue={(option: any) => option.label}
                                                value={{
                                                    id: OtherSettingFormik?.values?.fwind,
                                                    label: OtherSettingFormik?.values?.fwind
                                                }}
                                                onChange={(e: any) => OtherSettingFormik?.setFieldValue('fwind', e?.label)}
                                                onBlur={OtherSettingFormik?.handleBlur}
                                                styles={customStyle}
                                                isDisabled={!OtherSettingFormik?.values?.is_wind_assistance_system_available || isConfigurationButtonDisabled(vessel)}
                                            />
                                            {
                                                OtherSettingFormik?.errors && OtherSettingFormik?.touched &&
                                                OtherSettingFormik?.errors?.fwind && OtherSettingFormik?.touched?.fwind &&
                                                OtherSettingFormik?.values?.is_wind_assistance_system_available &&
                                                <ErrorTooltip
                                                    message={OtherSettingFormik?.errors?.fwind}
                                                    target={'fwind'}
                                                    open={!!OtherSettingFormik?.errors?.fwind}
                                                />
                                            }
                                        </Col>
                                        <Col sm={4}>
                                            <Label className='mb-0' for='pwind'>P<sub>wind</sub></Label>
                                            <div className='input-group'>
                                                <Input
                                                    onFocus={(e) => e.target.select()}
                                                    key={windRefreshkey}
                                                    type="text"
                                                    name="pwind"
                                                    id="pwind"
                                                    className="form-control max-width-7 text-right"
                                                    onChange={OtherSettingFormik?.handleChange}
                                                    onBlur={OtherSettingFormik?.handleBlur}
                                                    value={OtherSettingFormik?.values?.pwind || ''}
                                                    disabled={!OtherSettingFormik?.values?.is_wind_assistance_system_available || isConfigurationButtonDisabled(vessel)}
                                                />
                                                <div className='input-group-text'>kW</div>
                                            </div>
                                        </Col>
                                        <Col sm={4}>
                                            <Label className='mb-0' for='pprop'>P<sub>prop</sub></Label>
                                            <div className='input-group'>
                                                <Input
                                                    onFocus={(e) => e.target.select()}
                                                    key={windRefreshkey}
                                                    type="text"
                                                    name="pprop"
                                                    id="pprop"
                                                    className="form-control max-width-7 text-right"
                                                    onChange={OtherSettingFormik?.handleChange}
                                                    onBlur={OtherSettingFormik?.handleBlur}
                                                    value={OtherSettingFormik?.values?.pprop || ''}
                                                    disabled={!OtherSettingFormik?.values?.is_wind_assistance_system_available || isConfigurationButtonDisabled(vessel)}
                                                />
                                                <div className='input-group-text'>kW</div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                        <Row className='mb-2 mt-4'>
                            <Col sm={8} className="d-flex align-items-center">
                                <Label className='width-17 mb-0' for='is_steam_dump_valve_in_use'>
                                    Is steam dump valve used?<br />(Only for steam ship)
                                </Label>
                                <div className="square-switch sqswitch ms-2">
                                    <Input
                                        onFocus={(e) => e.target.select()}
                                        type="checkbox"
                                        switch="none"
                                        name="is_steam_dump_valve_in_use"
                                        id="is_steam_dump_valve_in_use"
                                        checked={OtherSettingFormik?.values?.is_steam_dump_valve_in_use}
                                        onChange={(e: any) => OtherSettingFormik?.setFieldValue("is_steam_dump_valve_in_use", e.target.checked)}
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                    />
                                    <Label
                                        for="is_steam_dump_valve_in_use"
                                        data-on-label='Yes'
                                        data-off-label='No'
                                        className='mb-0'
                                    />
                                </div>
                            </Col>
                        </Row>
                        {(vessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER ||
                            vessel?.vessel_type_precedence_id === VesselTypeConstant.GENERAL_CARGO_SHIP) &&
                            <React.Fragment>
                                <Row className='mb-2 mt-4'>
                                    <Col sm={6} className="d-flex align-items-center">
                                        <Label className='width-17 mb-0' for='is_container_available'>
                                            Does this vessel have a provision to carry containers?
                                        </Label>
                                        <div className="square-switch sqswitch ms-2">
                                            <Input
                                                onFocus={(e) => e.target.select()}
                                                type="checkbox"
                                                switch="none"
                                                name="is_container_available"
                                                id="is_container_available"
                                                onChange={(e: any) => {
                                                    OtherSettingFormik?.setFieldValue("is_container_available", e.target.checked);
                                                    if (!e.target.checked) {
                                                        OtherSettingFormik?.setFieldValue("reefer_container_available", false);
                                                        setReeferRefreshKey(reeferRefreshKey + 1);
                                                    }
                                                }}
                                                defaultChecked={
                                                    vessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER ? true :
                                                        OtherSettingFormik?.values?.is_container_available
                                                }
                                                disabled={vessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER || isConfigurationButtonDisabled(vessel)}
                                            />
                                            <Label
                                                for="is_container_available"
                                                data-on-label='Yes'
                                                data-off-label='No'
                                                className='mb-0'
                                            />
                                        </div>
                                    </Col>
                                    <Col sm={4}>
                                        <Label className='width-17 mb-0 asteric' for='max_teu'>
                                            MAX TEU
                                        </Label>
                                        <div className='input-group'>
                                            <Input
                                                onFocus={(e) => e.target.select()}
                                                key={windRefreshkey}
                                                type="text"
                                                name="max_teu"
                                                id="max_teu"
                                                className="form-control max-width-7 text-right"
                                                onChange={OtherSettingFormik?.handleChange}
                                                onBlur={OtherSettingFormik?.handleBlur}
                                                defaultValue={OtherSettingFormik?.values?.max_teu}
                                                disabled={!OtherSettingFormik?.values?.is_container_available || isConfigurationButtonDisabled(vessel)}
                                            />
                                            {
                                                OtherSettingFormik?.errors && OtherSettingFormik?.touched &&
                                                OtherSettingFormik?.errors?.max_teu && OtherSettingFormik?.touched?.max_teu &&
                                                <ErrorTooltip
                                                    message={OtherSettingFormik?.errors?.max_teu}
                                                    target={'max_teu'}
                                                    open={!!OtherSettingFormik?.errors?.max_teu}
                                                />
                                            }
                                            <div className='input-group-text'>TEU</div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row className='mb-2 mt-4'>
                                    <Col sm={8} className="d-flex align-items-center">
                                        <Label className='width-17 mb-0' for='reefer_container_available'>
                                            Does this vessel have a provision to carry reefer container?
                                        </Label>
                                        <div className="square-switch sqswitch ms-2">
                                            <Input
                                                onFocus={(e) => e.target.select()}
                                                key={reeferRefreshKey}
                                                type="checkbox"
                                                switch="none"
                                                name="reefer_container_available"
                                                id="reefer_container_available"
                                                onChange={(e: any) => OtherSettingFormik?.setFieldValue("reefer_container_available", e.target.checked)}
                                                checked={OtherSettingFormik?.values?.reefer_container_available}
                                                disabled={!OtherSettingFormik?.values?.is_container_available || isConfigurationButtonDisabled(vessel)}
                                            />
                                            <Label
                                                for="reefer_container_available"
                                                data-on-label='Yes'
                                                data-off-label='No'
                                                className='mb-0'
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            </React.Fragment>
                        }
                        <Row>
                            <div className="d-flex flex-wrap gap-5 grp_justify_right">
                                <Button type="submit" onClick={() => {
                                    OtherSettingFormik.validateForm();
                                }} color="primary" className='btn_size4_5_cstm' disabled={isConfigurationButtonDisabled(vessel)}>Save</Button>
                                <Button type="reset" color="danger" className='btn_size4_5_cstm' disabled={isConfigurationButtonDisabled(vessel)}>Reset</Button>
                            </div>
                        </Row>
                        <FormValuesDebug values={[OtherSettingFormik?.values, OtherSettingFormik?.errors, OtherSettingFormik.initialValues, OtherSettingFormik.touched]} />
                    </Form>
                }
                <p> - Set values.</p>
            </CardBody>
        )
    }
}

export default Other
